/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grow} from '@material-ui/core';
import {SnackbarKey, useSnackbar} from 'notistack';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import GroupProperties, {
  getNotificationsAlertSoundEnabled,
  getUrgentNotificationsEnabled
} from '@ion/core/src/services/GroupProperties';
import {AlertSeverity} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import soundFile from '@ion/ion-suite/public/assets/audio/AlertNotification.mp3';
import {NotificationAlert} from '@/components/notifications/NotificationAlert';
import {hasAppNotificationAccess} from '@/security/Features';
import {
  markAsRead,
  removeToastNotification,
  removeUrgentEnabledNotification,
  setNotificationPanelState,
  setNotificationsPanelStatus
} from '@/store/actions/notifications';
import {reviewNotification} from '@/store/actions/tracking';
import {getUserNotificationsPreferences} from '@/store/helpers';
import {GlobalGroupReducerState} from '@/store/types';

export interface ToastNotificationsContainerType {
  cancelToastDismiss: any[];
  setCancelToastDismiss: Function;
}

/**
 * Checks if User has enabled preference to view notification pop-up of this type
 */
export const isNotificationTypeAvailable = (severity: AlertSeverity, preferences: any) => {
  const alertSeverityMap = {
    [AlertSeverity.CRITICAL] : preferences[Preference.showUrgentNotifications],
    [AlertSeverity.HIGH]     : preferences[Preference.showGeneralNotifications],
    [AlertSeverity.MEDIUM]   : preferences[Preference.showTimeRelatedNotifications],
    [AlertSeverity.LOW]      : preferences[Preference.showConnectivityNotifications],
    [AlertSeverity.SYSTEM]   : preferences[Preference.showScheduledEventsNotifications]
  };
  return Boolean(alertSeverityMap[severity]);
};

export const ToastNotificationsContainer = ({
  cancelToastDismiss,
  setCancelToastDismiss
}: ToastNotificationsContainerType) => {
  const canAccessNotification     = hasAppNotificationAccess();
  const dispatch                  = useDispatch();
  const globalGroupId             = useSelector(({globalGroup}: GlobalGroupReducerState) => globalGroup.globalGroupId);
  const detailsState              = useSelector(({notifications}) => notifications.panel.state);
  const latestToastNotifications  = useSelector(({notifications}) => notifications.latest);
  const user                      = useSelector(({login: {user}}) => user);
  const urgentToastNotifications  = useSelector(({notifications}) => notifications.urgent);
  const unreadToastNotifications  = useSelector(({notifications}) => notifications.unreadCount);
  const [exitingSnackBar, setExitingSnackbar]                  = useState(false);
  const activeToasts                                           = useRef([]);
  const {enqueueSnackbar, closeSnackbar}                       = useSnackbar();
  const {isPhone, isDesktop}                                   = useMedia();
  const [isUrgentNotificationsEnabled, setUrgentNotifications] = useState(true);
  const queuedToasts                                           = useRef([]);
  const [isAlarmPreferenceEnabled, setAlarmPreference]         = useState(false);
  const alarmSound                                             = useRef<HTMLAudioElement>(new Audio(soundFile));
  const userNotificationsPreferencesRef                        = useRef([]);

  useEffect(() => {
    if (canAccessNotification) {
      userNotificationsPreferencesRef.current = getUserNotificationsPreferences();
      async function loadCurrentGroupPreferences() {
        await GroupProperties.getInheritedProperties(globalGroupId);
        setUrgentNotifications(getUrgentNotificationsEnabled());
        setAlarmPreference(getNotificationsAlertSoundEnabled());
      }
      loadCurrentGroupPreferences();
    }
  }, [canAccessNotification, user]);

  /**
   * Checks, if urgent notification preferences are enabled.
   */
  const _isAlarmNeeded = () => {
    return isUrgentNotificationsEnabled && isAlarmPreferenceEnabled;
  };

  // Enable sound Alarm after page change,
  // if there are unacknowledged urgent notifications
  useEffect(() => {
    if (canAccessNotification && _isAlarmNeeded()) {
      _enableAlertSound();
    }
  }, [canAccessNotification, isAlarmPreferenceEnabled]);

  /**
   * Helper function to play/pause alarm sound, based on
   * unacknowledged notifications length.
   */
  const _enableAlertSound = () => {
    const unreadNotifications = urgentToastNotifications.filter(urgent => !urgent.isRead);
    if (unreadNotifications?.length > 0) {
      if (alarmSound.current.paused) {
        alarmSound.current.play();
      }
    } else {
      alarmSound.current.pause();
    }
  };

  // Setup Alarm sound prefrences at the first start.
  useEffect(() => {
    if (canAccessNotification) {
      alarmSound.current.loop = true;
    }
    // Disable Alarm sound on page exit
    return function cleanup() {
      alarmSound.current.pause();
    };
  }, [canAccessNotification]);

  // Enable sound Alarm until all active notifications be acknowledged.
  useEffect(() => {
    if (canAccessNotification && _isAlarmNeeded()) {
      _enableAlertSound();
    }
  }, [canAccessNotification, unreadToastNotifications]);

  /**
   * Helper function to process notification object and display Toast message.
   */
  const _processNotification = (notification: any, index: number, isUrgentEnabled: boolean = false) => {
    const {
      category,
      isRead,
      alertConfigurationName,
      severity = AlertSeverity.MEDIUM,
      createdOn,
      uuid,
      assetName,
      alertType
    } = notification;
    if (exitingSnackBar) return;
    const {title: alertTypeTitle} = alertType;
    const matchingClick           = detailsState.find(clicked => clicked.id === uuid);
    const isDetailsOpen           = matchingClick?.open;
    const isDetailsLoading        = matchingClick?.loading;
    const isUrgent                = severity === AlertSeverity.CRITICAL;

    const onToastExit = (key: SnackbarKey) => {
      key && closeSnackbar(key);
      dispatch(removeToastNotification(index));
      clearCancelDismiss(notification.uuid);
      removeActiveToast(notification.uuid, isUrgentEnabled);
      // this prevents a second snackbar from getting
      // queued immediately after the first one exits,
      // allowing for the transition to finish. This happens
      // unexpectedly when more than one notifications are
      // retrieved from the call to latest API
      setExitingSnackbar(true);
      setTimeout(() => {
        setExitingSnackbar(false);
      }, 1000);
    };

    const onUrgentToastExit = () => {
      clearCancelDismiss(notification.uuid);
      setExitingSnackbar(true);
      setTimeout(() => {
        setExitingSnackbar(false);
      }, 1000);
    };

    function cancelActiveToast(uuid) {
      if (!isUrgentEnabled) {
        const match = activeToasts.current.find(active => active[uuid]);
        if (match) {
          clearTimeout(match[uuid]);
        }
      }
    }

    function removeToast(uuid) {
      if (isUrgentEnabled) {
        removeActiveToast(uuid, isUrgentEnabled);
      }
    }

    if (isNotificationTypeAvailable(severity, userNotificationsPreferencesRef.current)) {
      if (isUrgentNotificationsEnabled) {
        const activeToast = activeToasts.current.find(active => active[uuid]);
        const queuedToast = queuedToasts.current.find(queued => queued[uuid]);
        if (activeToast || queuedToast) {
          return;
        }
      }
      const key = enqueueSnackbar(`${notification.assetName}`, {
        persist             : isUrgentEnabled,
        TransitionComponent : GrowTransition,
        transitionDuration  : {enter: 1000, exit: 1000},
        onExited            : function() {
          !isUrgentEnabled ? onToastExit(0) : onUrgentToastExit();
        },
        content : (key: SnackbarKey) => (
          <NotificationAlert id={key}
            toastId={uuid}
            type={alertTypeTitle}
            text={assetName}
            subText={alertConfigurationName}
            read={isRead}
            timeStamp={createdOn}
            isUrgent={isUrgent}
            onMoreClick={readMore}
            onLessClick={readLess}
            onReviewClick={onReviewNotification}
            moreInfoLoading={isDetailsLoading}
            showMoreInfo={isDetailsOpen}
            notification={notification}
            onDoubleClick={cancelActiveToast}
            onToastRemoved={removeToast}/>
        )
      });
      if (isUrgentEnabled) {
        activeToasts.current.push({
          [notification.uuid] : key
        });
      } else {
        activeToasts.current.push({
          [notification.uuid] : setTimeout(() => {
            onToastExit(key);
          }, 5000)
        });
      }
    }
  };

  /**
   * Helper function to process notifications in the queue and display them,
   * if there is available space on the screen.
   */
  const _processToastQueue = useCallback(() => {
    if (canAccessNotification) {
      if (isUrgentNotificationsEnabled) {
        // Only 3 toasts at once could be displayed (Control limitation)
        if (activeToasts.current.length < 3 && queuedToasts.current.length > 0) {
          const availableLength = (3 - activeToasts.current.length);
          const queueLength = availableLength > queuedToasts.current.length
            ? queuedToasts.current.length : availableLength;
          for (let i = 0; i < queueLength; i++) {
            _processNotification(queuedToasts.current.shift(), i, true);
          }
        }
      } else {
        for (let i = 0; i < queuedToasts.current.length; i++) {
          _processNotification(queuedToasts.current.shift(), i, false);
        }
      }
    }
  }, [activeToasts.current, canAccessNotification, queuedToasts.current]);

  /**
   * Helper function to check duplicates in Toast queues.
   */
  const _checkToastQueue = (uuid, severity) => {
    const activeToast = activeToasts.current.find(active => active[uuid]);
    const queuedToast = queuedToasts.current.find(queued => queued[uuid]);
    if (isUrgentNotificationsEnabled && (activeToast || queuedToast)) {
      return false;
    }
    if (!activeToast && !queuedToast) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (canAccessNotification && latestToastNotifications?.length > 0) {
      if (userNotificationsPreferencesRef.current?.[Preference.showNotifications]) {
        latestToastNotifications?.forEach((notification) => {
          if (_checkToastQueue(notification?.uuid, notification?.severity)) {
            queuedToasts.current.push(notification);
          }
        });
        _processToastQueue();
      }
    }
  }, [canAccessNotification, JSON.stringify(latestToastNotifications)]);

  useEffect(() => {
    if (canAccessNotification) {
      cancelToastDismiss.forEach(toast => {
        const match = activeToasts.current.find(active => active[toast]);
        if (match) {
          clearTimeout(match[toast]);
        }
        clearCancelDismiss(toast);
      });
    }
  }, [canAccessNotification, cancelToastDismiss]);

  function clearCancelDismiss(uuid) {
    dispatch(removeUrgentEnabledNotification(uuid));
    const cancelMatches = cancelToastDismiss.filter(dismiss => uuid !== dismiss);
    setCancelToastDismiss(cancelMatches);
  };

  function removeActiveToast(uuid, isUrgentEnabled) {
    const removedActive = activeToasts.current.filter(active => !active[uuid]);
    activeToasts.current.splice(0, activeToasts.current.length);
    activeToasts.current.push(...removedActive);
    if (isUrgentEnabled) {
      _processToastQueue();
    }
  };

  function GrowTransition(props) {
    return <Grow {...props} />;
  }

  function onReviewNotification(notification) {
    isDesktop && setCancelToastDismiss([...cancelToastDismiss, notification?.uuid]);
    isPhone && closeSnackbar(notification?.uuid);
    isPhone && dispatch(setNotificationsPanelStatus(false));
    dispatch(reviewNotification(notification));
    dispatch(markAsRead(notification));
  }

  function readMore(state) {
    dispatch(setNotificationPanelState(state));
  }
  function readLess(state) {
    dispatch(setNotificationPanelState(state));
  }

  return null;
};

export default ToastNotificationsContainer;
